const PMTILES_PREFIX = 'pmtiles://';
const GEOJSON_PREFIX = 'geojson://';
const MAP_SOURCE_ARCHIVE_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/archive(?:$|[?#])/;
const MAP_SOURCE_TILE_TEMPLATE_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/tiles\/\{z\}\/\{x\}\/\{y\}(?:$|[?#])/;
const MAP_SOURCE_GEOJSON_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/data\.geojson(?:$|[?#])/;

const cloneStyleRecord = <T extends Record<string, unknown>>(value?: T | null): T | null => {
  if (!value) {
    return null;
  }
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const extractMapRequestPathname = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('/')) {
    return trimmed;
  }
  try {
    return new URL(trimmed).pathname;
  } catch {
    return trimmed;
  }
};

const resolveSourceIdFromPrefixedRef = (value: string, prefix: string, runtimePattern: RegExp) => {
  if (!value.startsWith(prefix)) {
    return '';
  }

  const candidate = value.slice(prefix.length).split('?', 1)[0].trim();
  if (!candidate || candidate.includes('://') || candidate.startsWith('/')) {
    const runtimeMatch = extractMapRequestPathname(candidate).match(runtimePattern);
    return runtimeMatch?.[1] ? decodeURIComponent(runtimeMatch[1]) : '';
  }

  return candidate;
};

const extractMapSourceIdFromStyleSource = (styleSource?: Record<string, unknown> | null) => {
  if (!styleSource || typeof styleSource !== 'object') {
    return '';
  }

  const rawUrl = typeof styleSource.url === 'string' ? styleSource.url.trim() : '';
  const sourceIdFromUrl = resolveSourceIdFromPrefixedRef(rawUrl, PMTILES_PREFIX, MAP_SOURCE_ARCHIVE_PATTERN);
  if (sourceIdFromUrl) {
    return sourceIdFromUrl;
  }

  const rawData = typeof styleSource.data === 'string' ? styleSource.data.trim() : '';
  const sourceIdFromData = resolveSourceIdFromPrefixedRef(rawData, GEOJSON_PREFIX, MAP_SOURCE_GEOJSON_PATTERN);
  if (sourceIdFromData) {
    return sourceIdFromData;
  }

  const runtimeGeoJSONMatch = extractMapRequestPathname(rawData).match(MAP_SOURCE_GEOJSON_PATTERN);
  if (runtimeGeoJSONMatch?.[1]) {
    return decodeURIComponent(runtimeGeoJSONMatch[1]);
  }

  const tiles = Array.isArray(styleSource.tiles) ? styleSource.tiles : [];
  const tileTemplate = tiles.find((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  const tileMatch = tileTemplate
    ? extractMapRequestPathname(tileTemplate).match(MAP_SOURCE_TILE_TEMPLATE_PATTERN)
    : null;
  return tileMatch?.[1] ? decodeURIComponent(tileMatch[1]) : '';
};

const inferInternalSourceRefsFromLayerMetadata = (styleJson?: Record<string, unknown> | null) => {
  const refs = new Map<string, string>();
  const layers = Array.isArray(styleJson?.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];

  for (const layer of layers) {
    const alias = typeof layer?.source === 'string' ? layer.source.trim() : '';
    if (!alias || refs.has(alias)) {
      continue;
    }

    const metadata =
      layer.metadata && typeof layer.metadata === 'object' ? (layer.metadata as Record<string, unknown>) : null;
    const workbench =
      metadata?.workbench && typeof metadata.workbench === 'object'
        ? (metadata.workbench as Record<string, unknown>)
        : null;
    const sourceId =
      (typeof workbench?.source_id === 'string' && workbench.source_id.trim()) ||
      (typeof workbench?.sourceId === 'string' && workbench.sourceId.trim()) ||
      '';
    if (!sourceId) {
      continue;
    }
    refs.set(alias, sourceId);
  }

  return refs;
};

export const normalizeMapStyleSourcesForRequest = <T extends Record<string, unknown>>(styleJson?: T | null): T | null => {
  const clone = cloneStyleRecord(styleJson);
  if (!clone?.sources || typeof clone.sources !== 'object') {
    return clone;
  }

  const layerRefs = inferInternalSourceRefsFromLayerMetadata(clone);
  const styleSources = clone.sources as Record<string, Record<string, unknown>>;

  Object.entries(styleSources).forEach(([alias, source]) => {
    if (!source || typeof source !== 'object') {
      return;
    }

    const sourceId = extractMapSourceIdFromStyleSource(source) || layerRefs.get(alias) || '';
    if (!sourceId) {
      return;
    }

    const rawUrl = typeof source.url === 'string' ? source.url.trim() : '';
    const rawData = typeof source.data === 'string' ? source.data.trim() : '';
    const hasTiles = Array.isArray(source.tiles) && source.tiles.length > 0;
    const isGeoJSONSource =
      source.type === 'geojson' ||
      typeof source.data !== 'undefined' ||
      rawData.startsWith(GEOJSON_PREFIX) ||
      MAP_SOURCE_GEOJSON_PATTERN.test(extractMapRequestPathname(rawData));

    if (isGeoJSONSource && !rawUrl) {
      styleSources[alias] = {
        ...source,
        type: 'geojson',
        data: `${GEOJSON_PREFIX}${sourceId}`,
      };
      delete styleSources[alias].url;
      delete styleSources[alias].tiles;
      return;
    }

    if (source.type === 'vector' || rawUrl || hasTiles) {
      styleSources[alias] = {
        ...source,
        type: 'vector',
        url: `${PMTILES_PREFIX}${sourceId}`,
      };
      delete styleSources[alias].data;
      delete styleSources[alias].tiles;
      return;
    }

    styleSources[alias] = {
      ...source,
      type: 'geojson',
      data: `${GEOJSON_PREFIX}${sourceId}`,
    };
    delete styleSources[alias].url;
    delete styleSources[alias].tiles;
  });

  return clone;
};

export const rewriteMapStyleSourcesToRuntime = <T extends Record<string, unknown>>(
  styleJson: T | null | undefined,
  options: {
    buildMapSourceArchiveUrl: (id: string) => string;
    buildMapSourceGeoJSONDataUrl: (id: string) => string;
  },
): T | null => {
  const nextStyle = cloneStyleRecord(styleJson);
  if (!nextStyle?.sources || typeof nextStyle.sources !== 'object') {
    return nextStyle;
  }

  Object.entries(nextStyle.sources as Record<string, Record<string, unknown>>).forEach(([alias, rawSource]) => {
    if (!rawSource || typeof rawSource !== 'object') {
      return;
    }

    const sourceId = extractMapSourceIdFromStyleSource(rawSource);
    if (!sourceId) {
      return;
    }

    const rawData = typeof rawSource.data === 'string' ? rawSource.data.trim() : '';
    const isGeoJSONSource =
      rawSource.type === 'geojson' ||
      rawData.startsWith(GEOJSON_PREFIX) ||
      MAP_SOURCE_GEOJSON_PATTERN.test(extractMapRequestPathname(rawData));

    if (isGeoJSONSource) {
      (nextStyle.sources as Record<string, Record<string, unknown>>)[alias] = {
        ...rawSource,
        type: 'geojson',
        data: options.buildMapSourceGeoJSONDataUrl(sourceId),
      };
      delete (nextStyle.sources as Record<string, Record<string, unknown>>)[alias].tiles;
      delete (nextStyle.sources as Record<string, Record<string, unknown>>)[alias].url;
      return;
    }

    (nextStyle.sources as Record<string, Record<string, unknown>>)[alias] = {
      ...rawSource,
      type: 'vector',
      url: options.buildMapSourceArchiveUrl(sourceId),
    };
    delete (nextStyle.sources as Record<string, Record<string, unknown>>)[alias].tiles;
    delete (nextStyle.sources as Record<string, Record<string, unknown>>)[alias].data;
  });

  return nextStyle;
};
